// src/components/auth/RpBootGate.tsx
//
// H-2C2 WP-6 — the OIDC RP (BFF-model) boot gate.
//
// This is the flag-gated PARALLEL boot path to the existing Bearer-model
// `BootGate`. `AppBootGate` selects one or the other from
// `HANDOFF_OIDC_CRM_ENABLED` (`VITE_HANDOFF_OIDC_CRM`). RpBootGate is INERT
// unless that flag is ON; the existing `BootGate` is untouched.
//
// On mount it resolves the boot outcome from exactly one source:
//   1. a `?auth_recovery=<state>` query parameter — set by a failed
//      `/auth/callback` redirect — renders that recovery surface directly; or
//   2. otherwise `GET /auth/context` (the server-only, `crm_sid`-protected
//      endpoint). On 200 it HYDRATES the `bootContext` store (via the shared
//      `mapSwitchContextResponse` normalizer — the same one the legacy
//      `BootGate` uses) and only THEN renders the app shell; a canonical
//      failure → recovery surface.
//
// Render gating: the app shell renders ONLY after `setBootReady` has hydrated
// `bootContext`. A recovery outcome NEVER hydrates `bootContext` — the shell
// stays un-booted and the `RecoverySurface` renders instead. This matches the
// legacy `BootGate` contract: nothing protected renders without canonical
// context in the store.
//
// Retry policy (deterministic): ONLY `degraded` auto-retries, and only when
// RpBootGate itself called `/auth/context`. It retries at most
// MAX_DEGRADED_RETRIES times with a VISIBLE "reconnecting" state — never an
// invisible background loop — then surfaces a manual "Try Again". `auth_
// unavailable` (the handoff-disabled config surface) and every other state
// require an explicit user action. No state auto-retries silently.
//
// Token boundary: tokens never cross `/auth/context`; `mapSwitchContextResponse`
// copies only workspace/app/user/meta fields — no token can reach Redux.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { CRM_FEATURE_FLAGS } from '../../api/centralFeaturesApi';
import { fetchRpContext } from '../../api/rpContextApi';
import { fetchRpFeatures } from '../../api/rpFeaturesApi';
import { setBootLoading, setBootReady } from '../../store/slices/bootContextSlice';
import {
  setFeatureFlags,
  setFeatureFlagsError,
  setFeatureFlagsLoading,
} from '../../store/slices/featureFlagsSlice';
import { useAppDispatch, type AppDispatch } from '../../store/store';
import LoadingSpinner from '../common/LoadingSpinner';
import RecoverySurface from './RecoverySurface';
import { rpContextToBootReady } from './rpBootAdapter';
import {
  isRecoveryState,
  isTerminalNoReauthState,
  type RecoveryState,
  type SurfaceState,
} from './recoveryStates';

/** Bounded auto-retry budget for the transient `degraded` state. */
export const MAX_DEGRADED_RETRIES = 3;
/** Default pause between degraded retries — keeps "reconnecting" visible. */
const DEFAULT_RETRY_DELAY_MS = 400;
/** Query parameter a failed `/auth/callback` redirect uses to carry the state. */
const RECOVERY_PARAM = 'auth_recovery';

type View =
  | { kind: 'loading' }
  | { kind: 'retrying'; attempt: number }
  | { kind: 'ready' }
  | { kind: 'recovery'; recovery: SurfaceState; action: 'signin' | 'retry' | 'logout' };

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Initialize runtime feature flags for the RP/BFF boot path.
 *
 * The legacy `BootGate` evaluates flags by calling Central directly with a
 * browser-held Bearer token. The RP/BFF browser holds NO token, so evaluation
 * is delegated to the `crm_sid`-gated `/auth/features` BFF endpoint, which
 * evaluates with the session's server-held token.
 *
 * Fire-and-forget: this NEVER blocks boot (the app shell has already rendered)
 * and fails closed — any failure leaves every flag `false`, exactly like the
 * legacy path's fail-closed behavior.
 */
async function hydrateRpFeatureFlags(dispatch: AppDispatch): Promise<void> {
  dispatch(setFeatureFlagsLoading());
  const flags = await fetchRpFeatures(CRM_FEATURE_FLAGS);
  if (flags) {
    dispatch(setFeatureFlags(flags));
  } else {
    // INVARIANT: fail closed — all flags default to false.
    dispatch(setFeatureFlagsError());
  }
}

/** A surface state whose only forward path is a manual `/auth/context` retry. */
function isRetryable(state: SurfaceState): boolean {
  return state === 'degraded' || state === 'auth_unavailable';
}

/**
 * The single recovery action for a surface state:
 *   - a TERMINAL state (re-auth of the same account can't escape it) →
 *     `logout` (end the session, land on a clean login, use a different
 *     account) — otherwise "Sign in" just returns to the same surface;
 *   - a retryable transient (`degraded`/`auth_unavailable`) → `retry`;
 *   - everything else (expired/interrupted session) → `signin`.
 */
function actionFor(state: SurfaceState): 'signin' | 'retry' | 'logout' {
  if (isTerminalNoReauthState(state)) return 'logout';
  if (isRetryable(state)) return 'retry';
  return 'signin';
}

/**
 * Read and CONSUME the one-shot `?auth_recovery=` parameter. Stripping it
 * means a manual page refresh does not re-pin a stale recovery surface — the
 * refresh re-runs `GET /auth/context` instead. An unknown value fails closed
 * to `require_reauth`.
 */
function consumeRecoveryParam(): RecoveryState | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(RECOVERY_PARAM);
  if (!raw) return null;

  params.delete(RECOVERY_PARAM);
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
  return isRecoveryState(raw) ? raw : 'require_reauth';
}

interface RpBootGateProps {
  children: ReactNode;
  /** Pause between degraded retries (ms). Injectable so tests run instantly. */
  retryDelayMs?: number;
}

export default function RpBootGate({
  children,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
}: RpBootGateProps) {
  const dispatch = useAppDispatch();
  const [view, setView] = useState<View>({ kind: 'loading' });
  const cancelled = useRef(false);

  // Run the `/auth/context` boot fetch with the bounded degraded-retry loop.
  const boot = useCallback(async (): Promise<void> => {
    for (let attempt = 0; ; attempt += 1) {
      if (attempt === 0) {
        // Reflect the boot lifecycle in the store, exactly like the legacy
        // BootGate — children stay blocked until `setBootReady`.
        dispatch(setBootLoading());
        setView({ kind: 'loading' });
      } else {
        setView({ kind: 'retrying', attempt });
      }

      const result = await fetchRpContext();
      if (cancelled.current) return;

      if (result.ok) {
        const payload = rpContextToBootReady(result.context);
        if (!payload) {
          // Unusable context — fail closed, do NOT hydrate a half-ready shell.
          setView({ kind: 'recovery', recovery: 'malformed', action: 'signin' });
          return;
        }
        // Hydrate bootContext BEFORE flipping to `ready` — the app shell never
        // renders without canonical context in the store.
        dispatch(setBootReady(payload));
        setView({ kind: 'ready' });
        // Runtime feature-flag initialization for the RP path — the parity fix
        // for the legacy BootGate's `evaluateFeatureFlags`. Fire-and-forget:
        // it runs ONCE per successful boot (context resolved + workspace known),
        // never blocks the shell, and fails closed. On an RP workspace switch
        // the shell performs a full `/auth/start` navigation, so this whole
        // boot → context → features sequence reruns for the new workspace.
        void hydrateRpFeatureFlags(dispatch);
        return;
      }
      if (result.recovery === 'degraded' && attempt < MAX_DEGRADED_RETRIES) {
        await delay(retryDelayMs);
        if (cancelled.current) return;
        continue;
      }
      setView({
        kind: 'recovery',
        recovery: result.recovery,
        // An exhausted `degraded` budget and `auth_unavailable` offer a manual
        // retry; a terminal state (`access_revoked`/`workspace_closed`) offers
        // a full sign-out (re-auth would loop); every other state requires
        // sign-in. A recovery outcome never hydrates bootContext.
        action: actionFor(result.recovery),
      });
      return;
    }
  }, [retryDelayMs, dispatch]);

  useEffect(() => {
    cancelled.current = false;
    const fromCallback = consumeRecoveryParam();
    if (fromCallback) {
      // A callback-origin failure: re-fetching `/auth/context` cannot help (no
      // session was issued), so this path NEVER offers a retry (not even for
      // `degraded`). The forward path is a fresh sign-in — except for a
      // terminal state, where re-auth of the same account loops, so the action
      // is a full sign-out. It does NOT hydrate bootContext.
      setView({
        kind: 'recovery',
        recovery: fromCallback,
        action: isTerminalNoReauthState(fromCallback) ? 'logout' : 'signin',
      });
    } else {
      void boot();
    }
    return () => {
      cancelled.current = true;
    };
  }, [boot]);

  if (view.kind === 'ready') {
    return <>{children}</>;
  }

  if (view.kind === 'recovery') {
    return (
      <RecoverySurface
        recovery={view.recovery}
        action={view.action}
        onRetry={() => {
          void boot();
        }}
      />
    );
  }

  // loading | retrying — a visible, deterministic boot state.
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-500" data-testid="rp-boot-status">
        {view.kind === 'retrying'
          ? `Reconnecting… (attempt ${view.attempt} of ${MAX_DEGRADED_RETRIES})`
          : 'Connecting to your workspace…'}
      </p>
    </div>
  );
}
